import { getLogger } from '@logtape/logtape';
import { AxiosError, AxiosResponse } from 'axios';
import { delay } from 'es-toolkit';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import { ApiError, api } from '@/lib/api-client.ts';
import { trackCheckinOutcome } from '@/lib/checkin-telemetry.ts';

import { EnrichedCustomer, EnrichedLocation } from '@/models/EnrichedPrismicDocuments.ts';
import { ModalType } from '@/models/Modal.ts';
import { NotificationType } from '@/models/Notification.ts';
import { PractitionerDtoWrapper } from '@/models/PractitionerDto.ts';
import { Room } from '@/models/Room.ts';
import { PractitionerData } from '@/models/admin/PractitionerData.ts';
import {
    Appointment,
    AppointmentBookStatus,
    AppointmentCancelReason,
    CollectionStatus,
    CollectionType,
    DailyAppointment,
} from '@/models/appointment/Appointment.ts';
import { BookAppointmentDto } from '@/models/appointment/BookAppointmentDto.ts';
import { EnrichedAppointmentBookingFormValues } from '@/models/appointment/EnrichedAppointmentBookingFormValues.ts';
import { EnrichedPractitionerData, Practitioner } from '@/models/appointment/EnrichedPractitionerData.ts';
import { GetAppointmentsForTodayResponseDto } from '@/models/appointment/GetAppointmentsForTodayResponseDto.ts';
import { GetAppointmentsResponse } from '@/models/appointment/GetAppointmentsResponse.ts';
import { UpdateAppointmentDto } from '@/models/appointment/UpdateAppointmentDto.ts';
import { AppointmentMoveOutcome } from '@/models/appointment/appointment-move-outcome.ts';
import {
    AppointmentCollection,
    BookAppointmentChainDto,
    BookAppointmentCollectionDto,
    UpdateAppointmentCollectionDto,
} from '@/models/appointment/book-appointment-collection-dto.ts';
import { StackSelectionResponse, StackSelectionResult } from '@/models/call-system/StackSelection.ts';
import {
    CheckinIntent,
    CheckinOutcome,
    CheckinOutcomeKind,
    OUTCOME_METRIC,
    type TerminalCheckinOutcome,
} from '@/models/call-system/check-in.ts';
import { UserAppointmentDto } from '@/models/call-system/dtos/user-appointment-dto.ts';
import { CheckinStatus, CheckoutStatus, UserStatus } from '@/models/call-system/models/CallSystemUser.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { DCScheduleType } from '@/models/doc-cirrus/DCScheduleType.ts';
import FormattedForm from '@/models/forms/FormattedForm.ts';
import { InsuranceType } from '@/models/insurance.ts';
import { type CheckinAttempt, type CheckinAttribution, CheckinEntry, CheckinType } from '@/models/metrics.ts';
import { ProfessionalTypeDocument, ServiceOrTreatmentDocument } from '@/models/prismicTypes.ts';

import { bookAppointmentSyncResponseSchema } from '@/schemas/book-appointment-sync-response.schema.ts';
import { bookAppointmentCollectionResponseSchema } from '@/schemas/chain-booking.schema.ts';
import { SchedulerInsuranceInfoSchema } from '@/schemas/validate-insurance-response.schema.ts';

import { getCurrentStacksForProfessional, updateUserByNumber } from '@/services/callSystemService.ts';
import { generateCode } from '@/services/formDataService.ts';
import { closeModal } from '@/services/modalService.tsx';
import {
    hideNotification,
    showErrorNotification,
    showLoadingNotification,
    showSuccessNotification,
    showWarningNotification,
    updateNotification,
} from '@/services/notificationService.ts';
import { validateInsuranceUser } from '@/services/patientService.ts';
import { getPVSServiceStrategy } from '@/services/pvsStrategy/strategySelector.ts';

import { isCheckinAttemptFresh, useAppointmentCheckinStore } from '@/stores/appointmentCheckinStore.ts';
import { BookingBatch, EnrichedAppointmentWithBookingStatus, useBookingStore } from '@/stores/bookingStore.ts';
import { useConfigStore } from '@/stores/configStore.ts';
import { usePrinterStore } from '@/stores/printerStore.ts';
import { useWalkInStore } from '@/stores/walkInStore.ts';

import {
    appendPatientContext,
    createAppointmentDtoFromForm,
    formToAppointment,
    getInstanceId,
    getRoomId,
    getUpdateAppointmentDtoFromFormData,
    isEternoAppointment,
} from '@/utils/appointmentUtils.ts';
import { resolveBookingErrorMessage } from '@/utils/booking-error-utils.ts';
import { getSelectedLocation } from '@/utils/configUtils.ts';
import { getCustomerId } from '@/utils/customer-utils.ts';
import { isToday } from '@/utils/date-utils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { prepareGenerateCodeBody } from '@/utils/formUtils.ts';
import { insuranceToInsuranceType } from '@/utils/insurance-utils.ts';
import { serializeApiError } from '@/utils/log-utils.ts';
import { openAppointmentScheduler } from '@/utils/modal/appointmentSchedulerUtils.tsx';
import { openStackSelectionModal, openTicketNumberModal } from '@/utils/modalUtils.tsx';
import { compareObjectsAndReturnDifferentFields } from '@/utils/objectUtils.ts';
import { extractUniquePracticesFromLinkedUsers } from '@/utils/practitioner/cachedDataUtils.ts';
import { prepareCheckinData, printTicket, updatePcs } from '@/utils/printUtils.ts';

import { ServiceUrl } from './ServiceURL.ts';

const logger = getLogger(['service', 'appointment']);

enum UserDataType {
    LINKED_USERS = 'linked_users',
    PRISMIC_PROFILES = 'prismic_profiles',
    PRISMIC_HUBS = 'prismic_hubs',
    PRISMIC_CUSTOMER_CONFIGS = 'prismic_customer_configs',
    PRISMIC_PROFESSIONAL_TYPES = 'prismic_professional_types',
    PRISMIC_SERVICES_OR_TREATMENTS = 'prismic_services_or_treatments',
}

enum UserDataTypeOption {
    INCLUDE_UNMAPPED_SCHEDULE_TYPES = 'include_unmapped_schedule_types',
}

interface AvailabilityAPIResponse {
    data: PractitionerDtoWrapper;
}

interface ScheduleTypesResponse {
    data: DCScheduleType[];
}

interface OnBookOrEditAppointmentOptions {
    send_notification?: boolean;
}

export const onBookOrEditAppointment = async (
    customer: EnrichedCustomer,
    location: EnrichedLocation,
    forms: EnrichedAppointmentBookingFormValues[],
    forms_options: EnrichedPractitionerData,
    instance_id: string,
    options?: OnBookOrEditAppointmentOptions
): Promise<void> => {
    const { send_notification } = options ?? {};
    const { getCurrentBatch, current_patient, addAppointmentsToBatch, editing_appointment } =
        useBookingStore.getState();

    const current_batch = getCurrentBatch();
    if (!current_batch) {
        logger.debug('there is no data to submit');
        return;
    }

    useBookingStore.setState({
        is_booking_loading: true,
        current_appointment_or_draft_id: undefined,
        patient_duplication_allowed: false,
    });

    const notification_id = showLoadingNotification(
        `Menge: ${forms.length}`, // Quantity
        `${current_patient?.personal.last_name} ${current_patient?.personal.first_name}`
    );

    // Add appointment to batch list to keep track about booking status
    const enriched_appointments: EnrichedAppointmentWithBookingStatus[] = forms.map((form) => ({
        form_id: form.form_id,
        status: AppointmentBookStatus.PENDING_BOOKING,
        form_data: form,
    }));
    addAppointmentsToBatch(current_batch.batch_id, notification_id, enriched_appointments);

    if (editing_appointment) {
        if (forms.length !== 1) {
            logger.debug('Error editing appointment has {formCount} !== 1', { formCount: forms.length });
            return;
        }

        // TODO: handle insurance check in case of Insurance update
        await onEditingFormSubmit(
            current_batch.batch_id,
            forms[0],
            forms_options,
            editing_appointment,
            notification_id,
            send_notification
        );
    } else {
        await onBookingFormsSubmit(
            current_batch.batch_id,
            forms,
            forms_options,
            customer?.customer_id,
            location,
            instance_id
        );
    }

    useBookingStore.setState({ appointments_form_draft: [], is_booking_loading: false });
};

interface CreateAppointmentBookingPromiseResult {
    form_id: string;
    booked_appointment?: Appointment;
    err_msg?: string;
}

const createAppointmentBookingPromise = async (
    form_id: string,
    dto: BookAppointmentDto,
    customer_id: string,
    instance_id: string,
    batch_id: string
): Promise<CreateAppointmentBookingPromiseResult> => {
    const { current_patient, updateAppointmentBookingStatus } = useBookingStore.getState();

    try {
        const booked_appointment: Appointment = await bookAppointment(customer_id, instance_id, dto);

        // The endpoint returns the final BOOKED/DECLINED outcome.
        // Fall back to DECLINED (rather than throwing) if the response body is empty/malformed.
        const final_status = booked_appointment ? booked_appointment.status : AppointmentBookStatus.DECLINED;
        updateAppointmentBookingStatus(batch_id, form_id, final_status, booked_appointment);
        finalizeBookingBatchIfComplete(batch_id, current_patient);

        return { form_id, booked_appointment };
    } catch (error) {
        // Resolve a specific user-facing message from the structured backend error (past-date or
        // slot-conflict), falling back to the raw error message when the code isn't recognized.
        const err_msg: string = resolveBookingErrorMessage({
            err: error,
            defaultMessage: (error as Error).message,
        });
        updateAppointmentBookingStatus(batch_id, form_id, AppointmentBookStatus.DECLINED, undefined, err_msg);
        finalizeBookingBatchIfComplete(batch_id, current_patient);

        return { form_id, err_msg };
    }
};

const onBookingFormsSubmit = async (
    batch_id: string,
    forms: EnrichedAppointmentBookingFormValues[],
    forms_options: EnrichedPractitionerData,
    customer_id: string,
    location: EnrichedLocation,
    instance_id: string
): Promise<void> => {
    const { current_patient, updateAppointmentBookingStatus } = useBookingStore.getState();

    useBookingStore.setState({
        appointments_form_draft: [],
    });

    try {
        // Step 1: Preprocess form values into DTOs with form_id
        const apts_dto_form_id: { dto: BookAppointmentDto; form_id: string }[] = [];
        for (const form_value of forms) {
            const apt_dto = createAppointmentDtoFromForm(form_value, forms_options, location, current_patient);
            if (!apt_dto) {
                updateAppointmentBookingStatus(batch_id, form_value.form_id, AppointmentBookStatus.DECLINED);
                continue;
            }
            apts_dto_form_id.push({ dto: apt_dto, form_id: form_value.form_id });
        }

        // Step 2: Create a list of promises for booking
        const booking_promises: Promise<CreateAppointmentBookingPromiseResult>[] = apts_dto_form_id.map(
            ({ form_id, dto }) => createAppointmentBookingPromise(form_id, dto, customer_id, instance_id, batch_id)
        );

        // Step 3: Wait for all promises to complete
        await Promise.all(booking_promises);
    } finally {
        // Every form already reached a terminal state — either the endpoint resolved them, a booking
        // exception was already caught and finalized, or every form was declined above before any booking
        // promise ever ran. finalizeBookingBatchIfComplete is idempotent (see finalizedBookingNotificationIds),
        // so it's safe to call unconditionally here without risking a duplicate or a stale overwrite.
        finalizeBookingBatchIfComplete(batch_id, current_patient);
    }
};

const onEditingFormSubmit = async (
    batchId: string,
    form: EnrichedAppointmentBookingFormValues,
    formsOptions: EnrichedPractitionerData,
    oldAppointment: DailyAppointment,
    notificationId: string,
    sendNotification?: boolean
): Promise<void> => {
    const { updateAppointmentBookingStatus, initial_form_values: initialFormValues } = useBookingStore.getState();

    let isSuccessful: boolean = false;
    let message: string = '';
    let updatedAppointment: Appointment | undefined;

    try {
        const appointmentData = formToAppointment(form, formsOptions);
        if (!appointmentData) {
            throw new Error('Issue on rendering collapsed appointment');
        }

        const updatedFields = compareObjectsAndReturnDifferentFields(initialFormValues ?? {}, form);
        const appointmentDto = getUpdateAppointmentDtoFromFormData(
            appointmentData,
            updatedFields,
            oldAppointment,
            sendNotification
        );
        [isSuccessful, message] = await updateAppointment(oldAppointment, appointmentDto);

        if (isSuccessful) {
            const isEternoApp = isEternoAppointment(oldAppointment);

            try {
                const userId = isEternoApp ? oldAppointment.user_id : (oldAppointment.patient?.pvs?.user_id ?? '');
                updatedAppointment = await getAppointmentById(oldAppointment.id, userId);
            } catch (fetchError) {
                logger.warn('Failed to fetch updated appointment after successful update', {
                    error: fetchError,
                    ...serializeApiError(fetchError),
                });
                // Keep status BOOKED even if fetch fails (update succeeded)
                updatedAppointment = undefined;
            }
        }
    } catch (error) {
        logger.error('Error updating appointment', { error: error, ...serializeApiError(error) });
        message = resolveBookingErrorMessage({
            err: error,
            defaultMessage: 'Fehler beim Aktualisieren des Termins',
        });
    } finally {
        if (isSuccessful) {
            updateAppointmentBookingStatus(batchId, form.form_id, AppointmentBookStatus.BOOKED, updatedAppointment);
        } else {
            updateAppointmentBookingStatus(batchId, form.form_id, AppointmentBookStatus.DECLINED, undefined, message);
        }
        updateNotification(isSuccessful ? NotificationType.SUCCESS : NotificationType.ERROR, {
            id: notificationId,
            message: isSuccessful ? 'Termin aktualisiert' : message,
        });
    }
};

/**
 * Fetch all practitioner infos from availability cached data.
 */
export const fetchPractitionerByCustomerId = async (customer_id: string): Promise<PractitionerDtoWrapper> => {
    try {
        // Make the API request
        const response: AxiosResponse<AvailabilityAPIResponse> = await api.get(
            ServiceUrl.PRACTITIONER_API_DOMAIN.SECURE_ADMIN.USER.AVAILABILITY_CACHED_DATA,
            {
                headers: {
                    customer_id: customer_id,
                },
                params: {
                    data_type: UserDataType.LINKED_USERS,
                    [UserDataTypeOption.INCLUDE_UNMAPPED_SCHEDULE_TYPES]: 'true',
                },
            }
        );

        return response.data.data;
    } catch (error) {
        // TODO: make this better
        throw new Error(`Wrong API call: ${String(error)}`);
    }
};

/**
 * Fetch all practitioners from multiple customers.
 *
 * This function will fetch all practitioners from availability cached data
 * for multiple customers and return an array of objects containing
 * the practitioners for each customer.
 *
 */
export const fetchPractitionersFromMultipleCustomers = async (customer_ids: string[]): Promise<PractitionerData[]> => {
    const settled = await Promise.allSettled(
        customer_ids.map((customer_id) => fetchPractitionerByCustomerId(customer_id))
    );

    const result = [];
    for (const [index, settled_item] of settled.entries()) {
        const customer_id = customer_ids[index];
        if (settled_item.status === 'fulfilled') {
            result.push({
                dc_prac_users: settled_item.value.linked_dc_users,
                data_cur_prac_users: settled_item.value.linked_data_cur_users,
                practices: extractUniquePracticesFromLinkedUsers(settled_item.value.linked_dc_users, customer_id),
            });
        }
    }

    return result;
};

/**
 * Fetch all prismic professional types from availability cached data.
 */
export const fetchProfessionalTypesByCustomerId = async (customer_id: string): Promise<ProfessionalTypeDocument[]> => {
    try {
        // Make the API request
        const response: AxiosResponse<AvailabilityAPIResponse> = await api.get(
            ServiceUrl.PRACTITIONER_API_DOMAIN.SECURE_ADMIN.USER.AVAILABILITY_CACHED_DATA,
            {
                headers: {
                    customer_id: customer_id,
                },
                params: {
                    data_type: UserDataType.PRISMIC_PROFESSIONAL_TYPES,
                },
            }
        );

        return response.data.data?.prismic_data?.professional_types ?? [];
    } catch (error) {
        // TODO: make this better
        throw new Error(`Wrong API call: ${String(error)}`);
    }
};

export const fetchAllScheduleTypes = async (
    customer_id: string,
    instance_id: string,
    pvs_provider: PVSProvider
): Promise<DCScheduleType[]> => {
    try {
        const { api_url, params } =
            getPVSServiceStrategy(pvs_provider).createPvsAppointmentTypesFetchRequest(instance_id);
        // Make the API request
        const response: AxiosResponse<ScheduleTypesResponse | undefined> = await api.get(api_url, {
            headers: {
                customer_id: customer_id,
            },
            params: params,
        });

        return response.data?.data ?? [];
    } catch (error) {
        throw new Error(`Error fetching schedule types: ${String(error)}`);
    }
};

/**
 * Fetch all prismic service or treatment types from availability cached data.
 */
export const fetchServiceOrTreatmentTypesByCustomerId = async (
    customer_id: string
): Promise<ServiceOrTreatmentDocument[]> => {
    try {
        // Make the API request
        const response: AxiosResponse<AvailabilityAPIResponse> = await api.get(
            ServiceUrl.PRACTITIONER_API_DOMAIN.SECURE_ADMIN.USER.AVAILABILITY_CACHED_DATA,
            {
                headers: {
                    customer_id: customer_id,
                },
                params: {
                    data_type: UserDataType.PRISMIC_SERVICES_OR_TREATMENTS,
                },
            }
        );

        return response.data.data?.prismic_data?.services_or_treatments ?? [];
    } catch (error) {
        // TODO: make this better
        throw new Error(`Wrong API call: ${String(error)}`);
    }
};

export const fetchAppointmentsForDate = async (
    customer_id: string,
    instance_id: string,
    location_key: string,
    selected_date: Date | undefined
): Promise<GetAppointmentsForTodayResponseDto> => {
    try {
        if (!customer_id || !instance_id) {
            throw new Error(`missing customer id or instance id`);
        }

        // today by default
        const localized_date: string = dayjs(selected_date).format('YYYY-MM-DD');

        // Make the API request
        const response: {
            data: GetAppointmentsForTodayResponseDto;
        } = await api.get(ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.GET_APPOINTMENTS_FOR_TODAY, {
            headers: {
                customer_id: customer_id,
            },
            params: {
                instance_id: instance_id,
                day: localized_date,
                location_key: location_key,
            },
        });

        return response.data;
    } catch (error) {
        // TODO: make this better
        throw new Error(`Wrong API call: ${String(error)}`);
    }
};

export const fetchAppointmentsByDateRange = async (
    customer_id: string,
    instance_id: string,
    start_date: string,
    end_date: string,
    location_key: string
): Promise<GetAppointmentsForTodayResponseDto> => {
    if (!customer_id || !instance_id) {
        throw new Error(`missing customer id or instance id`);
    }

    try {
        const response: {
            data: GetAppointmentsForTodayResponseDto;
        } = await api.get(ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.GET_APPOINTMENTS_BY_DATE_RANGE, {
            headers: { customer_id: customer_id },
            params: {
                instance_id: instance_id,
                start_date: start_date,
                end_date: end_date,
                location_key: location_key,
            },
        });

        return response.data;
    } catch (error) {
        throw new Error(`Error fetching appointments by date range: ${String(error)}`);
    }
};

export const fetchAppointmentsCheckInData = async (
    customer_id: string,
    instance_id: string,
    date: string
): Promise<UserAppointmentDto[]> => {
    try {
        const response: AxiosResponse<UserAppointmentDto[]> = await api.get(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.CALL_SYSTEM_API}/users/day`,
            {
                headers: {
                    customer_id: customer_id,
                },
                params: {
                    instance_id: instance_id,
                    day: dayjs(date).format('YYYY-MM-DD'),
                },
            }
        );

        const checkin_data: UserAppointmentDto[] = response.data;

        return checkin_data.filter((entry) => entry.user_status === UserStatus.KNOWN);
    } catch (error) {
        throw new Error(`Error fetching check-in data: ${String(error)}`);
    }
};

export const updateAppointment = async (
    appointment: Appointment,
    fields: Partial<UpdateAppointmentDto>,
    error_message?: string
): Promise<[boolean, string]> => {
    let api_url: string = '';
    let params: Record<string, string> = {};
    let body: object = {};

    if (isEternoAppointment(appointment)) {
        api_url = ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.UPDATE_APPOINTMENT;
        params = {
            appointment_id: appointment.id,
            user_id: appointment.user_id,
            instance_id: getInstanceId(appointment) || '',
        };
        body = {
            ...fields,
            pvs: {
                pvs_provider: appointment.pvs_provider,
                doc_cirrus: { ...fields.pvs?.doc_cirrus },
                data_cur: { ...fields.pvs?.data_cur },
            },
        };
    } else {
        const pvs_strategy = getPVSServiceStrategy(appointment.pvs_provider);
        ({ body, api_url, params } = pvs_strategy.createAppointmentUpdateRequest(appointment, fields));
    }

    try {
        if (appointment.is_external) {
            await api.post(api_url, body, { headers: { customer_id: getCustomerId() }, params: params });
        } else {
            await api.patch(api_url, body, { headers: { customer_id: getCustomerId() }, params: params });
        }

        return [true, `Termin aktualisiert`];
    } catch (error) {
        const fallback =
            error_message ??
            (error as { data?: { error?: string } })?.data?.error ??
            'Fehler beim Aktualisieren des Termins';
        const message = resolveBookingErrorMessage({ err: error, defaultMessage: fallback });

        return [false, message];
    }
};

/**
 * Updates only the insurance coverage type for an appointment.
 * Always targets the Eterno endpoint (not the PVS-strategy route) because
 * insurance_coverage_type is an Eterno-side field and does not need to sync back to DocCirrus.
 * Notification sending is suppressed unconditionally.
 */
export const updateAppointmentInsuranceType = async (
    appointment: Appointment,
    insuranceType: InsuranceType
): Promise<void> => {
    const body: Partial<UpdateAppointmentDto> = {
        patient: { insurance: insuranceType },
        appointment: { send_notification: false },
        pvs: { pvs_provider: appointment.pvs_provider, doc_cirrus: {}, data_cur: {} },
    };
    await api.patch(ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.UPDATE_APPOINTMENT, body, {
        headers: { customer_id: getCustomerId() },
        params: {
            appointment_id: appointment.id,
            user_id: appointment.user_id,
            instance_id: getInstanceId(appointment) ?? '',
        },
    });
};

export const getAppointmentById = async (appointment_id: string, user_id: string): Promise<Appointment> => {
    try {
        if (!appointment_id) {
            throw new Error(`missing appointment_id`);
        }

        // Make the API request
        const response: {
            data: Appointment;
        } = await api.get(`${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.APPOINTMENTS}/${appointment_id}/`, {
            headers: {
                customer_id: getCustomerId(),
            },
            params: {
                user_id: user_id,
            },
        });

        return response.data;
    } catch (error) {
        throw new Error(`Error fetching appointment data: ${String(error)}`);
    }
};

export const getPatientAppointmentsByDay = async (
    pvs_user_id: string,
    pvs_provider: PVSProvider,
    instance_id: string,
    day: string,
    location_key: string
): Promise<Appointment[]> => {
    try {
        const response: { data: GetAppointmentsResponse } = await api.get(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.APPOINTMENTS}/`,
            {
                headers: {
                    customer_id: getCustomerId(),
                },
                params: {
                    pvs_user_id: pvs_user_id,
                    pvs_provider: pvs_provider,
                    instance_id: instance_id,
                    day: day,
                    location_key: location_key,
                },
            }
        );

        return [...response.data.data.active, ...response.data.data.inactive];
    } catch (error) {
        throw new Error(`Error fetching appointment data: ${String(error)}`);
    }
};

const bookAppointment = async (
    customer_id: string,
    instance_id: string,
    appointmentBookingData: BookAppointmentDto
): Promise<Appointment> => {
    try {
        if (!customer_id || !instance_id) {
            throw new Error(`missing customer id or instance id`);
        }

        // Make the API request
        const response: {
            data: Appointment;
        } = await api.post(
            ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.BOOK_APPOINTMENT_SYNC,
            appointmentBookingData,
            {
                headers: {
                    customer_id: customer_id,
                },
                params: {
                    instance_id: instance_id,
                    user_id: appointmentBookingData.patient.id,
                },
            }
        );

        // The endpoint's contract only ever resolves to BOOKED/DECLINED — reject anything else
        const parsed = bookAppointmentSyncResponseSchema.safeParse(response.data);
        if (!parsed.success) {
            throw new ApiError(
                502,
                `Invalid sync booking response status: ${String((response.data as { status?: unknown })?.status)}`,
                parsed.error
            );
        }

        return response.data;
    } catch (error) {
        if (error instanceof ApiError) throw error;
        throw new Error(`Error on booking appointment API call: ${String(error)}`);
    }
};

export const createAppointmentSeries = async (
    customerId: string,
    userId: string,
    dto: BookAppointmentCollectionDto
): Promise<z.infer<typeof bookAppointmentCollectionResponseSchema>> => {
    if (!customerId || !userId) {
        throw new Error(`missing customer id or user id`);
    }

    const response = await api.post<unknown>(ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.BOOK_COLLECTION, dto, {
        headers: { customer_id: customerId },
        params: { user_id: userId },
    });

    const parsed = bookAppointmentCollectionResponseSchema.safeParse(response.data);
    if (!parsed.success) {
        throw new ApiError(502, 'Invalid series booking response', parsed.error);
    }
    const isInvalidResponse =
        parsed.data.collection.type !== CollectionType.SERIES ||
        (parsed.data.collection.booking_status === CollectionStatus.SUCCESS &&
            parsed.data.pre_booked_appointments.length !== dto.appointments.length);
    if (isInvalidResponse) {
        throw new ApiError(502, 'Invalid series booking response');
    }
    return parsed.data;
};

export interface AppointmentCollectionResponse {
    data: {
        appointments: Appointment[];
        collection: AppointmentCollection;
    };
    meta: {
        count: number;
    };
}

const updateCollectionResponseSchema = z.object({
    collection: z.object({
        id: z.string(),
        instance_id: z.string(),
        customer_id: z.string(),
        user_id: z.string(),
        type: z.nativeEnum(CollectionType),
        booking_status: z.nativeEnum(CollectionStatus),
    }),
    appointments: z.array(
        z.object({
            id: z.string(),
            start: z.string(),
            end: z.string(),
            status: z.string(),
            user_id: z.string(),
            customer_id: z.string(),
        })
    ),
});

type UpdateCollectionResponse = z.infer<typeof updateCollectionResponseSchema>;

export const updateAppointmentSeries = async (
    customerId: string,
    userId: string,
    dto: UpdateAppointmentCollectionDto
): Promise<UpdateCollectionResponse> => {
    if (!customerId || !userId) {
        throw new Error(`missing customer id or user id`);
    }

    const response = await api.post(ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.UPDATE_COLLECTION, dto, {
        headers: { customer_id: customerId },
        params: { user_id: userId },
    });

    const parsed = updateCollectionResponseSchema.safeParse(response.data);
    if (!parsed.success) {
        logger.error('Invalid update collection response', { error: parsed.error, ...serializeApiError(parsed.error) });
        throw new Error('Invalid update collection response from server');
    }

    return parsed.data;
};

export const getAppointmentCollection = async (
    collectionId: string,
    instanceId: string,
    customerId: string
): Promise<AppointmentCollectionResponse> => {
    if (!collectionId || !instanceId || !customerId) {
        throw new Error('Missing required parameters for fetching appointment collection');
    }

    try {
        const response = await api.get<AppointmentCollectionResponse>(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.GET_COLLECTION}/${collectionId}`,
            {
                headers: { customer_id: customerId },
                params: { instance_id: instanceId },
            }
        );

        return response.data;
    } catch (error) {
        if (error instanceof AxiosError) {
            throw new Error(`Error fetching appointment collection: ${error.message}`);
        }
        // Rethrow non-API errors
        throw error;
    }
};

export const cancelAppointment = async (
    customer_id: string,
    instance_id: string,
    appointment: Appointment | DailyAppointment,
    reason: AppointmentCancelReason
): Promise<void> => {
    let api_url: string = '';
    let headers: Record<string, string | undefined> = {};
    let params: Record<string, string | undefined> = {};

    const is_eterno_appointment = isEternoAppointment(appointment);
    logger.debug('Cancel appointment. Is eterno appointment: {isEternoAppointment}', {
        isEternoAppointment: is_eterno_appointment,
    });

    if (is_eterno_appointment) {
        api_url = `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.APPOINTMENTS}/${appointment.id}/cancel`;
        headers = { customer_id: customer_id };
        params = {
            instance_id: instance_id,
            user_id: appointment.user_id,
            reason: reason,
        };
    } else {
        const pvs_strategy = getPVSServiceStrategy(appointment.pvs_provider);
        ({ api_url, headers, params } = pvs_strategy.createAppointmentCancelRequest(
            customer_id,
            instance_id,
            appointment,
            reason
        ));
    }

    await api.post(api_url, appointment, { headers, params });
};

const cancelledItemSchema = z.object({
    appointment_id: z.string(),
    start: z.string(),
    end: z.string(),
});

const parseCancelledItems = (responseData: unknown): { id: string; start: string; end: string }[] => {
    const rawItems = (responseData as { cancelled?: unknown })?.cancelled;
    if (!Array.isArray(rawItems)) {
        const typeLabel = rawItems === null ? 'null' : typeof rawItems;
        throw new ApiError(502, `Expected cancelled array in response, got: ${typeLabel}`);
    }

    const validItems = rawItems.flatMap((item, index) => {
        const parsed = cancelledItemSchema.safeParse(item);
        if (parsed.success) return [{ id: parsed.data.appointment_id, start: parsed.data.start, end: parsed.data.end }];
        logger.warn('Invalid cancelled item at index {index}', {
            index: index,
            error: parsed.error,
            ...serializeApiError(parsed.error),
        });
        return [];
    });

    return validItems;
};

export interface CancelledItem {
    id: string;
    start: string;
    end: string;
}

export const cancelAppointmentsCollection = async (
    customerId: string,
    instanceId: string,
    collectionId: string | undefined,
    reason: AppointmentCancelReason
): Promise<CancelledItem[]> => {
    if (!collectionId) {
        throw new ApiError(
            400,
            'Fehler beim Löschen der Terminserie: Ungültige Serien-ID. Bitte versuchen Sie es später erneut.'
        );
    }

    const response = await api.post(
        `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.CANCEL_COLLECTION}/${collectionId}`,
        {},
        {
            headers: { customer_id: customerId },
            params: { instance_id: instanceId, reason: reason },
        }
    );

    return parseCancelledItems(response.data);
};

interface PrepareAndCheckinArrivedParams {
    appointment: DailyAppointment;
    selected_room: Room['room_id'];
    forms_for_appointment: FormattedForm[] | undefined;
    practitioner: Practitioner | undefined;
    intent: CheckinIntent;
    existing_ticket?: string;
    entry: CheckinEntry;
}

// The effective check-in mode: `auto` only when the flow will actually auto-dispatch (auto-checkin
// configured, an active/new-checkin-capable call system, and no forms at all). A toggle-on flow that
// falls back to a manual confirmation (inactive call system or any forms) is reported as `manual`.
// The forms predicate must stay identical to the one `checkinArrived` uses to decide `isAutoDispatch`,
// otherwise the reported mode and the mode actually run can disagree.
const resolveCheckinType = (
    appointment: DailyAppointment,
    appointmentForms: FormattedForm[] | undefined
): CheckinType => {
    const { is_config_auto_checkin } = useAppointmentCheckinStore.getState();
    const config = getSelectedLocation().config_record[getInstanceId(appointment) ?? ''];
    const hasNoForms = !appointmentForms || appointmentForms.length === 0;
    const isAutoDispatch =
        (is_config_auto_checkin ?? false) &&
        Boolean(config?.is_patient_call_system_active) &&
        Boolean(config?.is_new_checkin_available) &&
        hasNoForms;
    return isAutoDispatch ? 'auto' : 'manual';
};

/**
 * The still-open attempt for this appointment, if any. An attempt past the reuse window was abandoned, so
 * it is cleared instead of returned: its stale stamp must not be resumed or timed. Sole reader of the
 * stored stamp, so resuming and emitting cannot disagree on what "open" means.
 */
const takeFreshCheckinAttempt = (appointmentId: DailyAppointment['id']): CheckinAttempt | undefined => {
    const checkinStore = useAppointmentCheckinStore.getState();
    const attempt = checkinStore.getCheckinAttempt(appointmentId);
    if (attempt === undefined) return undefined;
    if (isCheckinAttemptFresh(attempt)) return attempt;

    checkinStore.clearCheckinAttempt(appointmentId);
    return undefined;
};

/**
 * Opens the attempt this call belongs to, reusing a still-open one so the duration and attribution
 * (entry, checkin_type) follow the initiating action across a reload or a manual arrive→confirm.
 *
 * Returned so the caller can reuse it as fallback attribution: a flow that outlives the reuse window
 * (e.g. the stack-selection modal left open) finds no stored stamp at emission time, and must still be
 * counted.
 */
const startOrResumeCheckinAttempt = ({
    appointment,
    entry,
    forms_for_appointment,
}: PrepareAndCheckinArrivedParams): CheckinAttempt => {
    const existing = takeFreshCheckinAttempt(appointment.id);
    if (existing !== undefined) return existing;

    const attempt: CheckinAttempt = {
        started_at: Date.now(),
        entry: entry,
        checkin_type: resolveCheckinType(appointment, forms_for_appointment),
    };
    useAppointmentCheckinStore.getState().setCheckinAttempt(appointment.id, attempt);
    return attempt;
};

export const prepareAndCheckinArrived = async (params: PrepareAndCheckinArrivedParams): Promise<CheckinOutcome> => {
    const { appointment } = params;
    const attempt = startOrResumeCheckinAttempt(params);

    if (!isToday(appointment.day)) {
        showErrorNotification('Man kann nur Termine vom heutigen Tag verschieben.'); // Can only move appointments of today
        const outcome: CheckinOutcome = { kind: CheckinOutcomeKind.FAILED, reason: 'not_today' };
        emitCheckinOutcome(appointment.id, outcome, attempt);
        return outcome;
    }

    const outcome = await resolveCheckinOutcome(params);
    emitCheckinOutcome(appointment.id, outcome, attempt);
    return outcome;
};

// Resolves the terminal check-in outcome (arrive → optional dispatch / check-in). The service owns the
// CHECKED_IN write; callers react to the returned outcome.
const resolveCheckinOutcome = async ({
    appointment,
    selected_room,
    forms_for_appointment,
    practitioner,
    intent,
    existing_ticket,
}: PrepareAndCheckinArrivedParams): Promise<CheckinOutcome> => {
    if (existing_ticket) {
        return checkinArrived({
            appointment: appointment,
            selected_room: selected_room,
            ticket: existing_ticket,
            formatted_forms_for_appointment: forms_for_appointment,
            preSelectedStackId: undefined,
            intent: intent,
        });
    }

    const forms_to_fill = forms_for_appointment?.filter((form) => !form.is_completed);

    const { is_config_auto_checkin } = useAppointmentCheckinStore.getState();
    const autoCheckinEnabled = is_config_auto_checkin ?? false;

    const instance_id = getInstanceId(appointment) ?? '';
    const selected_location = getSelectedLocation();
    const config = selected_location.config_record[instance_id];

    const is_call_system_active = config?.is_patient_call_system_active;
    const is_new_checkin_available = config?.is_new_checkin_available;
    const has_no_forms = !forms_to_fill || forms_to_fill.length === 0;

    // Stack selection (auto-dispatch flow)
    const needs_stack_selection =
        autoCheckinEnabled && is_call_system_active && is_new_checkin_available && has_no_forms;

    if (needs_stack_selection) {
        // Resolve stacks before any printing or updates
        const availableStacks = await resolveAvailableStacks(appointment.professional.id, practitioner?.pcs_stack_id);

        if (availableStacks.length === 0) {
            // No stacks: continue without dispatch
            showWarningNotification('Kein Zimmer für automatische Weiterleitung gefunden. Bitte manuell zuweisen.');
            return proceedWithCheckin(
                appointment,
                selected_room,
                forms_to_fill,
                config,
                selected_location,
                autoCheckinEnabled,
                undefined,
                intent
            );
        }

        // Stacks available: let the user pick before printing the ticket
        const response = await new Promise<StackSelectionResponse>((resolve) =>
            openStackSelectionModal(availableStacks, resolve)
        );

        if (response.result === StackSelectionResult.CANCELLED) {
            return { kind: CheckinOutcomeKind.CANCELLED };
        }

        const selectedStackId =
            response.result === StackSelectionResult.SKIP_DISPATCH ? undefined : response.selectedStack;

        if (response.result === StackSelectionResult.SKIP_DISPATCH) {
            showWarningNotification('Automatische Ticketweiterleitung wird übersprungen. Bitte manuell zuweisen.');
        }

        return proceedWithCheckin(
            appointment,
            selected_room,
            forms_to_fill,
            config,
            selected_location,
            autoCheckinEnabled,
            selectedStackId,
            intent
        );
    }

    // Non-dispatch flow (manual checkin or has forms)
    return proceedWithCheckin(
        appointment,
        selected_room,
        forms_to_fill,
        config,
        selected_location,
        autoCheckinEnabled,
        undefined,
        intent
    );
};

const isTerminalCheckinOutcome = (outcome: CheckinOutcome): outcome is TerminalCheckinOutcome =>
    OUTCOME_METRIC[outcome.kind] !== undefined;

/**
 * Maps a terminal outcome onto the metric vocabulary. `durationMs` is omitted when the start is unknown,
 * so the check-in is still counted without skewing the duration. Fire-and-forget.
 */
const trackTerminalCheckinOutcome = (
    outcome: TerminalCheckinOutcome,
    { entry, checkin_type }: CheckinAttribution,
    durationMs: number | undefined
): void =>
    trackCheckinOutcome({
        outcome: OUTCOME_METRIC[outcome.kind],
        checkinType: checkin_type,
        entry: entry,
        reason: outcome.kind === CheckinOutcomeKind.FAILED ? outcome.reason : undefined,
        durationMs: durationMs,
    });

/**
 * Emits one `careflow.checkin.outcome` per completed / failed / aborted check-in (plus
 * `careflow.checkin.duration`), attributed to the attempt that started it. ARRIVED / AWAITING_FORMS are
 * intermediate and emit nothing — the attempt stays open so the completing action reports the full
 * arrive→confirm duration.
 *
 * A failure also keeps the attempt open, so a retry is still timed from the original start; only success
 * and abort consume it. With no open attempt the outcome is counted from `fallbackAttribution` when given,
 * and without a duration.
 */
const emitCheckinOutcome = (
    appointmentId: DailyAppointment['id'],
    outcome: CheckinOutcome,
    fallbackAttribution?: CheckinAttribution
): void => {
    if (!isTerminalCheckinOutcome(outcome)) return;

    const attempt = takeFreshCheckinAttempt(appointmentId);
    if (attempt === undefined) {
        if (fallbackAttribution !== undefined) trackTerminalCheckinOutcome(outcome, fallbackAttribution, undefined);
        return;
    }

    if (outcome.kind !== CheckinOutcomeKind.FAILED) {
        useAppointmentCheckinStore.getState().clearCheckinAttempt(appointmentId);
    }
    trackTerminalCheckinOutcome(outcome, attempt, Date.now() - attempt.started_at);
};

/**
 * Reports the outcome of a check-in written outside `prepareAndCheckinArrived` — the confirm step of an
 * arrive→confirm flow (the CTA, or dragging an already-arrived patient into a treatment room). The arrive
 * step is intermediate and reports nothing, so without this those flows would only ever emit failures.
 *
 * Reuses the open attempt when there is one, so the duration spans arrive→confirm; otherwise the outcome
 * is counted without a duration and as `manual`, since a patient left in ARRIVED was not auto-dispatched.
 */
export const reportCheckinCompletion = (
    appointmentId: DailyAppointment['id'],
    entry: CheckinEntry,
    outcome: CheckinOutcome
): void => emitCheckinOutcome(appointmentId, outcome, { entry: entry, checkin_type: 'manual' });

const proceedWithCheckin = async (
    appointment: DailyAppointment,
    selected_room: Room['room_id'],
    forms_to_fill: FormattedForm[] | undefined,
    config: ReturnType<typeof getSelectedLocation>['config_record'][string],
    selected_location: ReturnType<typeof getSelectedLocation>,
    is_config_auto_checkin: boolean,
    preSelectedStackId: string | undefined,
    intent: CheckinIntent
): Promise<CheckinOutcome> => {
    if (config.is_patient_call_system_active && is_config_auto_checkin) {
        const selectedPrinter = usePrinterStore.getState().selected_printer;

        const [success, printedTicket] = await printTicket(config, selectedPrinter[selected_location.key]);
        if (!success || !printedTicket) {
            showWarningNotification(
                'Beim Drucken ist ein Problem aufgetreten. Bitte führen Sie einen manuellen Check-in durch.'
            );
            return { kind: CheckinOutcomeKind.FAILED, reason: 'print_failed' };
        }

        return checkinArrived({
            appointment: appointment,
            selected_room: selected_room,
            ticket: printedTicket,
            formatted_forms_for_appointment: forms_to_fill,
            preSelectedStackId: preSelectedStackId,
            intent: intent,
        });
    }

    if (config.is_patient_call_system_active) {
        const modalTicket = await new Promise<string | undefined>((resolve) => {
            openTicketNumberModal(
                (ticket) => {
                    resolve(ticket);
                    return Promise.resolve();
                },
                () => resolve(undefined)
            );
        });

        if (!modalTicket) {
            return { kind: CheckinOutcomeKind.CANCELLED };
        }

        return checkinArrived({
            appointment: appointment,
            selected_room: selected_room,
            ticket: modalTicket,
            formatted_forms_for_appointment: forms_to_fill,
            preSelectedStackId: undefined,
            intent: intent,
        });
    }

    return checkinArrived({
        appointment: appointment,
        selected_room: selected_room,
        ticket: uuidv4(),
        formatted_forms_for_appointment: forms_to_fill,
        preSelectedStackId: undefined,
        intent: intent,
    });
};

// Builds and sends the appointment's forms code from whatever forms it receives; it does not filter.
// Called only when some form are still incomplete.
const generateFormsCode = async (
    selected_location: ReturnType<typeof getSelectedLocation>,
    appointment: DailyAppointment,
    appointmentFormattedForms: FormattedForm[] | undefined
): Promise<void> => {
    const generateCodeBody = prepareGenerateCodeBody(
        selected_location,
        appointment,
        (appointmentFormattedForms ?? []).map((form) => ({
            id: form.id,
            key: form.template_key ?? '',
        }))
    );
    if (generateCodeBody) {
        await generateCode(getCustomerId(), generateCodeBody);
    }
};

// Performs the terminal CHECKED_IN write, dispatching to the pre-selected stack first when required.
const checkinWithOptionalDispatch = async (
    appointment: DailyAppointment,
    ticket: string,
    config: ReturnType<typeof getSelectedLocation>['config_record'][string],
    selectedLocation: ReturnType<typeof getSelectedLocation>,
    isAutoDispatch: boolean,
    preSelectedStackId: string | undefined
): Promise<CheckinOutcome> => {
    if (isAutoDispatch && preSelectedStackId) {
        const selectedPrinter = usePrinterStore.getState().selected_printer;
        const successPcs = await updatePcs(config, selectedPrinter[selectedLocation.key], ticket, preSelectedStackId);
        if (!successPcs) {
            showWarningNotification('Ein Problem ist bei der Weiterleitung aufgetreten.');
            return { kind: CheckinOutcomeKind.FAILED, reason: 'pcs_dispatch_failed' };
        }
    }

    const checkedInSuccess = await checkin(appointment, ticket);
    if (!checkedInSuccess) {
        showErrorNotification('Beim Einchecken ist ein Problem aufgetreten');
        return { kind: CheckinOutcomeKind.FAILED, reason: 'checkin_call_failed' };
    }

    return { kind: CheckinOutcomeKind.CHECKED_IN, ticket: ticket };
};

interface CheckinArrivedParams {
    appointment: DailyAppointment;
    selected_room: Room['room_id'];
    ticket: string;
    formatted_forms_for_appointment: FormattedForm[] | undefined;
    preSelectedStackId: string | undefined;
    intent: CheckinIntent;
}

export const checkinArrived = async ({
    appointment,
    selected_room,
    ticket,
    formatted_forms_for_appointment,
    preSelectedStackId,
    intent,
}: CheckinArrivedParams): Promise<CheckinOutcome> => {
    if (!isToday(appointment.day)) {
        showErrorNotification('Man kann nur Termine vom heutigen Tag verschieben.'); // Can only move appointments of today
        return { kind: CheckinOutcomeKind.FAILED, reason: 'not_today' };
    }

    if (selected_room !== getRoomId(appointment)) {
        const [update_room_status, error_message] = await updateAppointment(
            appointment,
            { appointment: { room_id: selected_room, ticket: ticket } },
            'Ein Problem ist aufgetreten, als der Patient in den Raum verlegt wurde.'
        );
        if (!update_room_status) {
            showErrorNotification(error_message);
            return { kind: CheckinOutcomeKind.FAILED, reason: 'room_update_failed' };
        }
    }

    const selected_location = getSelectedLocation();
    const instance_id = getInstanceId(appointment) ?? '';
    const config = selected_location.config_record[instance_id];

    const arrivedData = prepareCheckinData(
        appointment,
        selected_location,
        config,
        ticket,
        CheckinStatus.ARRIVED,
        CheckoutStatus.PENDING
    );
    const [arrivedSuccess] = await updateUserByNumber(arrivedData);

    if (!arrivedSuccess) {
        showErrorNotification('Beim Einchecken ist ein Problem aufgetreten');
        return { kind: CheckinOutcomeKind.FAILED, reason: 'checkin_update_failed' };
    }

    const { is_config_auto_checkin } = useAppointmentCheckinStore.getState();
    const is_call_system_active = config?.is_patient_call_system_active;
    const is_new_checkin_available = config?.is_new_checkin_available;
    const has_no_forms = !formatted_forms_for_appointment || formatted_forms_for_appointment.length === 0;
    const hasIncompleteForms = formatted_forms_for_appointment?.some((form) => !form.is_completed) ?? false;
    const isAutoDispatch = Boolean(
        is_config_auto_checkin && is_call_system_active && is_new_checkin_available && has_no_forms
    );

    // Generate the forms code whenever any form is still incomplete.
    if (hasIncompleteForms) {
        await generateFormsCode(selected_location, appointment, formatted_forms_for_appointment);
    }

    // The service is the SOLE owner of the terminal CHECKED_IN write: it happens when the
    // auto-dispatch config requires it, or when the caller explicitly intends a full check-in.
    const shouldCheckIn = isAutoDispatch || intent === CheckinIntent.CHECK_IN;
    if (shouldCheckIn) {
        return checkinWithOptionalDispatch(
            appointment,
            ticket,
            config,
            selected_location,
            isAutoDispatch,
            preSelectedStackId
        );
    }

    return hasIncompleteForms
        ? { kind: CheckinOutcomeKind.AWAITING_FORMS, ticket: ticket }
        : { kind: CheckinOutcomeKind.ARRIVED, ticket: ticket };
};

const resolveAvailableStacks = async (professionalId: string, pcsStackId: string | undefined): Promise<string[]> => {
    const stacks: Set<string> = new Set();

    if (pcsStackId) {
        stacks.add(pcsStackId);
    }

    const stackOptions = await getCurrentStacksForProfessional(professionalId);
    if (stackOptions) {
        for (const option of stackOptions.options) {
            stacks.add(option);
        }
    } else {
        showWarningNotification('Fehler beim Abrufen der Zimmeroptionen.');
    }

    return [...stacks];
};

export const checkin = async (appointment: Appointment, ticket: string): Promise<boolean> => {
    if (!isToday(appointment.day)) {
        showErrorNotification('Man kann nur Termine vom heutigen Tag verschieben.'); // Can only move appointments of today
        return false;
    }

    const selected_location = getSelectedLocation();
    const instance_id = getInstanceId(appointment) ?? '';
    const config = selected_location.config_record[instance_id];

    const checkout_data = prepareCheckinData(
        appointment,
        selected_location,
        config,
        ticket,
        CheckinStatus.CHECKED_IN,
        CheckoutStatus.PENDING
    );
    const [checkin_success] = await updateUserByNumber(checkout_data);

    return checkin_success;
};

export const checkoutAppointment = async (
    appointment: Appointment,
    ticket: string
): Promise<AppointmentMoveOutcome> => {
    if (!isToday(appointment.day)) {
        showErrorNotification('Man kann nur Termine vom heutigen Tag verschieben.'); // Can only move appointments of today
        return AppointmentMoveOutcome.FAILED;
    }

    const [update_room_status] = await updateAppointment(appointment, { appointment: { room_id: '' } });
    if (!update_room_status) {
        showErrorNotification(
            appendPatientContext('Beim Auschecken ist ein Problem aufgetreten', appointment),
            undefined,
            {
                autoClose: false,
            }
        );
        return AppointmentMoveOutcome.FAILED;
    }

    const selected_location = getSelectedLocation();
    const instance_id = getInstanceId(appointment) ?? '';
    const config = selected_location.config_record[instance_id];

    const checkout_data = prepareCheckinData(
        appointment,
        selected_location,
        config,
        ticket,
        CheckinStatus.CHECKED_IN,
        CheckoutStatus.CHECKED_OUT
    );
    const [checkout_success] = await updateUserByNumber(checkout_data);

    if (!checkout_success) {
        showErrorNotification(
            appendPatientContext('Beim Auschecken ist ein Problem aufgetreten', appointment),
            undefined,
            {
                autoClose: false,
            }
        );
        // Room was already cleared server-side; only the call-system write failed.
        return AppointmentMoveOutcome.PARTIAL;
    }

    return AppointmentMoveOutcome.SUCCESS;
};

export const moveAppointmentToExpected = async (
    appointment: Appointment,
    ticket: string
): Promise<AppointmentMoveOutcome> => {
    if (!isToday(appointment.day)) {
        showErrorNotification('Man kann nur Termine vom heutigen Tag verschieben.'); // Can only move appointments of today
        return AppointmentMoveOutcome.FAILED;
    }

    let roomCleared = false;
    if (getRoomId(appointment)) {
        const [update_room_status] = await updateAppointment(appointment, { appointment: { room_id: '' } });
        if (!update_room_status) {
            showErrorNotification(
                appendPatientContext('Es gab ein Problem beim Verschieben des Termins', appointment),
                undefined,
                { autoClose: false }
            );
            return AppointmentMoveOutcome.FAILED;
        }
        roomCleared = true;
    }

    const selected_location = getSelectedLocation();
    const instance_id = getInstanceId(appointment) ?? '';
    const config = selected_location.config_record[instance_id];

    const call_system_data = prepareCheckinData(
        appointment,
        selected_location,
        config,
        ticket,
        CheckinStatus.PENDING,
        CheckoutStatus.PENDING
    );
    const [call_system_success] = await updateUserByNumber(call_system_data);

    if (!call_system_success) {
        showErrorNotification(
            appendPatientContext('Es gab ein Problem beim Verschieben des Termins', appointment),
            undefined,
            { autoClose: false }
        );
        return roomCleared ? AppointmentMoveOutcome.PARTIAL : AppointmentMoveOutcome.FAILED;
    }

    return AppointmentMoveOutcome.SUCCESS;
};

export const updateTicketNumber = async (
    appointment: DailyAppointment,
    old_ticket: string,
    new_ticket: string,
    current_checkin_data: UserAppointmentDto
): Promise<[boolean, string]> => {
    if (!isToday(appointment.day)) {
        return [false, 'Man kann nur Ticket von heutigen Terminen ändern.'];
    }

    // 1. Update appointment record (syncs to DC Details)
    const [appointment_update_success, error_message] = await updateAppointment(
        appointment,
        { appointment: { ticket: new_ticket } },
        'Ein Problem ist beim Aktualisieren der Ticketnummer aufgetreten.'
    );

    if (!appointment_update_success) {
        return [false, error_message];
    }

    // 2. Update call system (waiting room display)
    const selected_location = getSelectedLocation();
    const instance_id = getInstanceId(appointment) ?? '';
    const config = selected_location.config_record[instance_id];

    const updated_checkin_data = prepareCheckinData(
        appointment,
        selected_location,
        config,
        new_ticket,
        current_checkin_data.appointment?.checkin_status ?? CheckinStatus.ARRIVED,
        current_checkin_data.appointment?.checkout_status
    );

    const [call_system_success] = await updateUserByNumber(updated_checkin_data);

    if (!call_system_success) {
        return [
            false,
            'Ticketnummer wurde im Termin aktualisiert, aber Aufrufanzeige konnte nicht aktualisiert werden.',
        ];
    }

    return [true, 'Ticketnummer erfolgreich aktualisiert'];
};

// A batch's completion can be independently noticed from multiple places (a settled sync result, a caught
// booking exception, and the onBookingFormsSubmit safety net) — track which notifications
// already showed their definitive result so we never show it (and log it to notification history) twice.
// Keyed by notification_id, not batch_id: the scheduler lets a user add another appointment to an already-
// finalized batch (BookingActionsBar's "Termin hinzufügen"), and each such submission gets its own fresh
// notification_id via addAppointmentsToBatch while reusing the same batch_id — keying by batch_id would
// permanently block finalization for every submission after the first.
const finalizedBookingNotificationIds = new Set<string>();

const finalizeBookingBatchIfComplete = (
    batch_id: string,
    current_patient?: { personal: { first_name: string; last_name: string } }
): void => {
    const batch: BookingBatch | undefined = useBookingStore.getState().getBatchById(batch_id);
    if (!batch) return;

    if (!batch.enriched_appointments.every((apt) => apt.status !== AppointmentBookStatus.PENDING_BOOKING)) {
        // Wait until the entire batch finish to update, otherwise we can update intermediate stare.
        return;
    }

    if (finalizedBookingNotificationIds.has(batch.notification_id)) return;
    finalizedBookingNotificationIds.add(batch.notification_id);

    const failed_booking = batch.enriched_appointments.some((apt) => apt.status === AppointmentBookStatus.DECLINED);
    const notification_update = {
        id: batch.notification_id,
        title: 'Booking completed',
        message: `${current_patient?.personal.last_name} ${current_patient?.personal.first_name}`,
        loading: false,
        withCloseButton: true,
        onClick: (): void => {
            logger.debug('on notification click');
            // Prevent to click on notification if modal is already open
            if (!useBookingStore.getState().is_open) {
                logger.debug('schedule is closed, should be opened');
                // Set context of the notification
                useConfigStore.setState({
                    selected_customer: batch.customer,
                    selected_location: batch.location,
                });
                // Open scheduler with correct batch
                openAppointmentScheduler({
                    mode: 'view',
                    existing_batch_id: batch.batch_id,
                });
                hideNotification(batch.notification_id);
            }
        },
    };

    if (!failed_booking && batch.enriched_appointments.every((apt) => apt.status === AppointmentBookStatus.BOOKED)) {
        // All appointments are booked
        notification_update.title = 'Buchung abgeschlossen';
        updateNotification(NotificationType.SUCCESS, notification_update);
    } else {
        // 1 or more appointment failed — surface the specific reason
        const declinedError = batch.enriched_appointments.find(
            (apt) => apt.status === AppointmentBookStatus.DECLINED && apt.error
        )?.error;
        notification_update.title = 'Die Buchung ist fehlgeschlagen';
        if (declinedError) notification_update.message = declinedError;
        updateNotification(NotificationType.ERROR, notification_update);
    }
};

export const getCorrectInsuranceForForms = async (
    forms: Omit<EnrichedAppointmentBookingFormValues, 'pvs_user_id'>[],
    customer_id: string,
    instance_id: string,
    user_id: string,
    allow_duplication: boolean
): Promise<{
    forms: EnrichedAppointmentBookingFormValues[];
    duplication_required: boolean;
    duplication_done: boolean;
}> => {
    useBookingStore.setState({ is_validating_insurance: true });
    const insurances_infos: SchedulerInsuranceInfoSchema[] = forms.map(({ insurance, practice_doc_cirrus_id }) => ({
        insurance_type: insuranceToInsuranceType(insurance),
        location_id: practice_doc_cirrus_id ?? '',
    }));

    const insurances_result = await validateInsuranceUser(customer_id, instance_id, user_id, {
        schedule_insurance_info: insurances_infos,
        allow_duplication: allow_duplication,
    });

    const forms_with_pvs_user_id: EnrichedAppointmentBookingFormValues[] = forms.map((form) => {
        // Find a dc_user that match location_id and insurance_type
        const pvs_user_id_matched: string | undefined = insurances_result.users_insurance_infos.find((info) =>
            info.insurances.some(
                (insurance) =>
                    insurance.insurance_type === insuranceToInsuranceType(form.insurance) &&
                    insurance.location_id === form.practice_doc_cirrus_id
            )
        )?.dc_user_id;

        // Attach the dc_user_id if found to the form
        return {
            ...form,
            form_id: form.form_id,
            pvs_user_id: pvs_user_id_matched || '',
            eterno_user_id: insurances_result.eterno_user_id,
        };
    });

    useBookingStore.setState({ is_validating_insurance: false });

    return {
        forms: forms_with_pvs_user_id,
        duplication_required: insurances_result.duplication_required,
        duplication_done: insurances_result.duplication_done,
    };
};

export const getUserAppointments = async (
    user_id?: string,
    pvs_user_id?: string,
    instance_id?: string,
    pvs_provider?: PVSProvider,
    location_key?: string
): Promise<{ active: Appointment[]; inactive: Appointment[] }> => {
    try {
        const api_base_url = ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.APPOINTMENTS;
        const params: Record<string, string> = {};

        // Add parameters only if they are defined
        if (user_id) params.user_id = user_id;
        if (pvs_user_id) params.pvs_user_id = pvs_user_id;
        if (instance_id) params.instance_id = instance_id;
        if (pvs_provider) params.pvs_provider = pvs_provider;
        if (location_key) params.location_key = location_key;

        const response: AxiosResponse<{ data: { active: Appointment[]; inactive: Appointment[] } }> = await api.get(
            `${api_base_url}`,
            {
                headers: {
                    customer_id: getCustomerId(),
                },
                params: params,
            }
        );

        return response.data.data;
    } catch (error) {
        throw new Error(`Error fetching user appointments: ${String(error)}`);
    }
};

export const fetchScheduleTypeColorMap = async (
    customer_id: string,
    instance_ids: string[]
): Promise<Record<string, string>> => {
    try {
        const promises = instance_ids.map((instance_id) =>
            fetchAllScheduleTypes(customer_id, instance_id, PVSProvider.DOC_CIRRUS)
        );

        const results: DCScheduleType[][] = await Promise.all(promises);

        const schedule_types: DCScheduleType[] = results.flat();

        const map: Record<string, string> = {};
        for (const schedule_type of schedule_types) {
            if (!schedule_type) {
                continue;
            }

            map[schedule_type.id] = schedule_type.color;
        }

        return map;
    } catch (error) {
        logger.error('Error fetching schedule types', { error: error, ...serializeApiError(error) });
        return {};
    }
};

export const addPatientToAppointment = async (mutate: () => void): Promise<void> => {
    const { current_patient: patient } = useBookingStore.getState();
    const { selected_appointment: selected_appointment_without_patient } = useWalkInStore.getState();

    if (!patient || !selected_appointment_without_patient) {
        return;
    }

    closeModal(ModalType.WALK_IN);

    const fields: Partial<UpdateAppointmentDto> = {
        patient_id: patient.pvs_user_id,
    };

    const notification_id = showLoadingNotification('Der Termin wird aktualisiert...');

    const [is_updated, error_message] = await updateAppointment(selected_appointment_without_patient, fields);

    // Wait 1.5 seconds before mutating
    await delay(1500);

    hideNotification(notification_id);
    if (is_updated) {
        mutate();
        showSuccessNotification('Der Termin wurde aktualisiert.');
        return;
    }

    showWarningNotification(`Der Termin konnte nicht aktualisiert werden. [${error_message}]`);
};

export const bookAppointmentChain = async (
    customerId: string,
    userId: string,
    dto: BookAppointmentChainDto
): Promise<z.infer<typeof bookAppointmentCollectionResponseSchema>> => {
    if (!customerId || !userId) {
        throw new ApiError(400, 'missing customer id or user id');
    }

    const response = await api.post<unknown>(ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.BOOK_COLLECTION, dto, {
        headers: { customer_id: customerId },
        params: { user_id: userId },
    });

    const parsed = bookAppointmentCollectionResponseSchema.safeParse(response.data);
    if (!parsed.success) {
        throw new ApiError(502, 'Invalid chain booking response', parsed.error);
    }
    return parsed.data;
};
